"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { useParams } from "next/navigation";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  Code,
  Database,
  Key,
  Megaphone,
  MessageSquare,
  Webhook,
} from "lucide-react";
import { renderMarkdown } from "~/components/inbound-gpt/render-markdown";
import { MarketingExploreCta } from "~/components/inbound-gpt/MarketingExploreCta";
import { SiteGlueBlogArticle } from "~/components/siteglue/blog/siteglue-blog-article";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { AiSummaryPageContent as SiteGlueAiSummaryPageContent } from "~/components/siteglue/ai-summary-page-content";
import { getMarketingChrome } from "~/lib/siteglue/marketing-chrome";
import { SITEGLUE_BLOG_INDEX } from "~/lib/siteglue/adapt-blog-for-siteglue";
import { adaptMarketingCopyForBrand } from "~/lib/siteglue/adapt-marketing-copy";

interface BlogPost {
  id: number;
  title: string;
  content: string;
  slug?: string;
  metaTitle?: string;
  metaDescription?: string;
  targetKeyword?: string;
  founderInsight?: string;
  type?: string;
  status?: string;
  createdAt: string;
}

interface Announcement {
  id: number;
  title: string;
  content: string;
  slug?: string;
  metaDescription?: string;
  createdAt: string;
}

function extractExcerpt(content: string, maxLen = 180): string {
  const plain = content
    .replace(/^#{1,6}\s+.*$/gm, "")
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/\*(.*?)\*/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/[-*]\s/g, "")
    .replace(/\|[^|]*\|/g, "")
    .replace(/\n+/g, " ")
    .trim();
  return plain.length > maxLen ? plain.substring(0, maxLen - 3) + "..." : plain;
}

/** Documentation UI lives in documentation-page.tsx (Replit-style docs shell). */

// export const metadata = {
//   title: "Blog — AI Healthcare Insights | Inbound GPT",
//   description: "Insights, tips, and updates on AI-powered patient communication for healthcare practices.",
// };

export function BlogPageContent() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const chrome = getMarketingChrome();
  const isSiteGlue = chrome.brandId === "siteglue";

  useEffect(() => {
    fetch("/api/blog/posts")
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setPosts(data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="pb-16 pt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-3 text-sm font-semibold uppercase tracking-wide text-primary"
            >
              {isSiteGlue ? SITEGLUE_BLOG_INDEX.brandLine : "Inbound GPT Blog"}
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl sm:text-5xl font-bold tracking-tight"
            >
              <span className="text-primary">Blog</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto"
            >
              {isSiteGlue
                ? SITEGLUE_BLOG_INDEX.subtitle
                : "Insights on never missing a conversation, AI Employee plans, and keeping a human in the loop."}
            </motion.p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {posts.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                  {posts.map((post, index) => (
                    <motion.div
                      key={post.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      data-testid={`card-blog-live-${post.id}`}
                    >
                      <Link
                        href={`/blog/${post.slug || post.id}`}
                        className="group rounded-md border border-border bg-card p-6 flex flex-col hover:border-primary transition-colors h-full"
                      >
                        <span className="text-xs font-semibold text-primary uppercase tracking-wider mb-2">Blog</span>
                        <h3 className="text-lg font-bold tracking-tight mb-2 group-hover:text-primary transition-colors">{post.title}</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed flex-1 mb-4">{extractExcerpt(post.content)}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">
                            {new Date(post.createdAt).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                          </span>
                          <span className="inline-flex items-center gap-1 text-xs text-primary font-medium">
                            Read <ArrowRight className="w-3 h-3" />
                          </span>
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              )}
            </>
          )}

          <div className="mt-16 text-center">
            <p className="text-muted-foreground">
              See how practices succeed with AI in our{" "}
              <Link href="/customer-stories" className="text-primary hover:underline" data-testid="link-blog-customer-stories">Customer Stories</Link>,{" "}
              or explore the{" "}
              <Link href="/documentation" className="text-primary hover:underline" data-testid="link-blog-documentation">Documentation</Link>{" "}
              to get started.
            </p>
          </div>
        </div>
      </div>
  );
}




// export const metadata = {
//   title: "Announcements | Inbound GPT",
//   description: "Product updates, new integrations, and platform announcements from the Inbound GPT team.",
// };

export function AnnouncementsPageContent() {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/api/announcements")
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setAnnouncements(data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="pb-16 pt-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <div className="flex items-center gap-2 mb-3">
              <Megaphone className="w-5 h-5 text-primary" />
              <span className="text-xs font-semibold text-primary uppercase tracking-wider">From the team</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">
              Announcements
            </h1>
            <p className="text-lg text-muted-foreground max-w-xl">
              {adaptMarketingCopyForBrand(
                "Product updates, new integrations, and platform news from the Inbound GPT team.",
                getMarketingChrome().brandId,
              )}
            </p>
          </motion.div>

          {loading ? (
            <div className="flex justify-center py-16">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : announcements.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              No announcements yet. Check back soon.
            </div>
          ) : (
            <div className="space-y-4">
              {announcements.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.06 }}
                  data-testid={`card-announcement-${item.id}`}
                >
                  <Link
                    href={`/blog/${(item.slug || String(item.id)).replace(/-announcement$/, "")}`}
                    className="group flex flex-col sm:flex-row sm:items-start gap-4 rounded-md border border-border bg-card p-6 hover:border-primary transition-colors"
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-primary/10 text-primary px-2.5 py-1 rounded-full whitespace-nowrap">
                        <Megaphone className="w-3 h-3" /> New
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <h2 className="text-base font-bold tracking-tight group-hover:text-primary transition-colors leading-snug">
                          {item.title}
                        </h2>
                        <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0 mt-0.5" />
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed mt-2">
                        {item.metaDescription || extractExcerpt(item.content, 220)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-3">
                        {new Date(item.createdAt).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                      </p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}

          <div className="mt-16 pt-8 border-t border-border text-center">
            <p className="text-sm text-muted-foreground">
              See all articles on our{" "}
              <Link href="/blog" className="text-primary hover:underline" data-testid="link-announcements-blog">
                Blog
              </Link>{" "}or{" "}
              <Link href="/about" className="text-primary hover:underline" data-testid="link-announcements-contact">
                contact us
              </Link>{" "}with questions.
            </p>
          </div>
        </div>
      </div>
  );
}




// export const metadata = {
//   title: "API Reference | Inbound GPT",
//   description: "Build custom integrations with the Inbound GPT API. Access conversations, appointments, contacts, and webhooks.",
// };

const endpoints = [
  {
    icon: MessageSquare,
    title: "Conversations API",
    description:
      "Retrieve, manage, and analyze conversations across chat, phone, SMS, email, and more.",
  },
  {
    icon: Calendar,
    title: "Appointments API",
    description: "Create, update, and manage appointments programmatically.",
  },
  {
    icon: Database,
    title: "Contacts API",
    description: "Access and manage contact records and communication history.",
  },
  {
    icon: Webhook,
    title: "Webhooks",
    description:
      "Receive real-time notifications for new conversations, appointments, and team handoffs.",
  },
  {
    icon: Key,
    title: "Authentication",
    description: "API key management, OAuth flows, and security best practices.",
  },
  {
    icon: Code,
    title: "SDKs & Libraries",
    description: "Client libraries for JavaScript, Python, and other popular languages.",
  },
];

export function ApiReferencePageContent() {
  const [selectedEndpoint, setSelectedEndpoint] = useState<string | null>(null);
  const brand = getMarketingChrome().brandId;
  const a = (s: string) => adaptMarketingCopyForBrand(s, brand);

  return (
    <div className="pb-16 pt-8">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-16 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl font-bold tracking-tight sm:text-5xl"
          >
            API <span className="text-primary">Reference</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground"
          >
            {a(
              "Build custom integrations with the Inbound GPT API — with humans in the loop for handoffs and approvals.",
            )}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {endpoints.map((endpoint, index) => (
            <motion.button
              key={endpoint.title}
              type="button"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="rounded-md border border-border bg-card p-6 text-left transition-colors hover:border-primary/40 hover:bg-primary/5"
              data-testid={`card-api-${endpoint.title.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setSelectedEndpoint(endpoint.title)}
            >
              <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-md bg-primary/10">
                <endpoint.icon className="h-5 w-5 text-primary" />
              </div>
              <h3 className="mb-2 text-lg font-bold tracking-tight">{endpoint.title}</h3>
              <p className="text-sm leading-relaxed text-muted-foreground">{endpoint.description}</p>
              <span className="mt-4 inline-flex text-xs font-semibold uppercase tracking-wide text-primary">
                Coming soon
              </span>
            </motion.button>
          ))}
        </div>

        <div className="mt-16 text-center">
          <p className="text-muted-foreground">
            Full API documentation coming soon. Contact us for early access.
          </p>
          <p className="mt-4 text-muted-foreground">
            Visit the{" "}
            <Link
              href="/documentation"
              className="text-primary hover:underline"
              data-testid="link-api-documentation"
            >
              Documentation
            </Link>{" "}
            for setup guides, or{" "}
            <Link href="/about" className="text-primary hover:underline" data-testid="link-api-contact">
              contact us
            </Link>{" "}
            for integration support.
          </p>
        </div>
      </div>

      <Dialog open={selectedEndpoint != null} onOpenChange={(open) => !open && setSelectedEndpoint(null)}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-api-coming-soon">
          <DialogHeader>
            <DialogTitle>Coming Soon</DialogTitle>
            <DialogDescription>
              {selectedEndpoint
                ? a(
                    `${selectedEndpoint} is not publicly available yet. We're building developer APIs that keep team handoffs and human-in-the-loop workflows first-class.`,
                  )
                : "This API is coming soon."}
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Contact us for early access, or explore product Documentation while we finish the public
            reference.
          </p>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" asChild>
              <Link href="/documentation">Documentation</Link>
            </Button>
            <Button type="button" asChild>
              <Link href="/about">Contact us</Link>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}




// export const metadata = {
//   title: "AI Summary — Inbound GPT Platform Overview",
//   description: "A structured overview of Inbound GPT's AI platform for healthcare practices, including features, use cases, integrations, specialties, and pricing.",
// };

/** @deprecated Prefer importing from ~/components/siteglue/ai-summary-page-content */
export function AiSummaryPageContent() {
  return <SiteGlueAiSummaryPageContent />;
}

export function BlogPostPageContent() {
  const params = useParams<{ slug: string }>();
  const slug = params?.slug;
  const chrome = getMarketingChrome();
  const isSiteGlue = chrome.brandId === "siteglue";
  const [post, setPost] = useState<BlogPost | null>(null);
  const [allPosts, setAllPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    const isNumeric = /^\d+$/.test(slug);
    const url = isNumeric
      ? `/api/blog/posts/${slug}`
      : `/api/blog/posts/by-slug/${encodeURIComponent(slug)}`;

    const postPromise = fetch(url).then((r) => {
      if (!r.ok) throw new Error("not found");
      return r.json() as Promise<BlogPost>;
    });
    const listPromise = isSiteGlue
      ? fetch("/api/blog/posts")
          .then((r) => (r.ok ? (r.json() as Promise<BlogPost[]>) : []))
          .catch(() => [] as BlogPost[])
      : Promise.resolve([] as BlogPost[]);

    Promise.all([postPromise, listPromise])
      .then(([data, list]) => {
        setPost(data);
        setAllPosts(Array.isArray(list) ? list : []);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug, isSiteGlue]);

  // Scroll to hash after markdown headings with ids are in the DOM (async-loaded posts).
  useEffect(() => {
    if (!post || typeof window === "undefined") return;
    const hash = window.location.hash.replace(/^#/, "");
    if (!hash) return;
    const t = window.setTimeout(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
    return () => window.clearTimeout(t);
  }, [post]);

  if (loading) {
    return (
      <div className="flex justify-center pb-16 pt-8">
        <div className="mt-16 h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="pb-16 pt-8">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <h1 className="mb-4 text-2xl font-bold">Post Not Found</h1>
          <p className="mb-6 text-muted-foreground">
            This article doesn&apos;t exist or has been removed.
          </p>
          <Link
            href="/blog"
            className="flex items-center justify-center gap-1 text-primary hover:underline"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  if (isSiteGlue) {
    return <SiteGlueBlogArticle post={post} allPosts={allPosts.length ? allPosts : [post]} />;
  }

  return (
    <div className="pb-16 pt-8">
        <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            href="/blog"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors"
            data-testid="button-back-to-blog"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-sm text-primary font-semibold uppercase tracking-wider mb-3">Blog</p>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4" data-testid="heading-blog-post">
              {post.title}
            </h1>
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-10 pb-6 border-b border-border">
              <Calendar className="w-4 h-4" />
              {new Date(post.createdAt).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
            </div>

            <div className="prose prose-lg max-w-none" data-testid="blog-post-content">
              {renderMarkdown(post.content.replace(/^#\s+.+\n?/, ""))}
            </div>

            <MarketingExploreCta className="mt-14" testId="blog-cta" />

            <div className="mt-10 pt-8 border-t border-border">
              <p className="text-sm font-semibold text-muted-foreground mb-4">Continue reading</p>
              <div className="flex flex-wrap gap-4">
                <Link
                  href="/front-desk-receptionist"
                  className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                  data-testid="link-front-desk-receptionist"
                >
                  Front Desk Receptionist <ArrowRight className="w-3.5 h-3.5" />
                </Link>
                <Link
                  href="/blog"
                  className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                  data-testid="link-back-blog"
                >
                  All Articles <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          </motion.div>
        </article>
      </div>
  );
}